import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
  onSnapshot,
  Unsubscribe,
  writeBatch,
  deleteField,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

export type TrustedContact = {
  id: string;
  name: string;
  phoneNumber: string;
  email?: string;
  relationship?: string; // e.g., "Emergency Contact", "Family", "Friend"
  isPrimary?: boolean;
  createdAt: Date;
  updatedAt: Date;
};

export type FirestoreTrustedContact = {
  name: string;
  phoneNumber: string;
  email?: string;
  relationship?: string;
  isPrimary?: boolean;
  createdAt: Timestamp | string;
  updatedAt: Timestamp | string;
};

/**
 * Subscribe to user's trusted contacts
 */
export const subscribeToTrustedContacts = (
  userId: string,
  callback: (contacts: TrustedContact[]) => void
): Unsubscribe => {
  const contactsRef = collection(db, 'users', userId, 'trustedContacts');
  
  let unsubscribeFn: (() => void) | null = null;
  let isCleanedUp = false;

  try {
    unsubscribeFn = onSnapshot(
      contactsRef,
      (snapshot) => {
        // Don't process updates if already cleaned up
        if (isCleanedUp) {
          return;
        }

        try {
          const contacts: TrustedContact[] = snapshot.docs.map((doc) => {
            const data = doc.data() as FirestoreTrustedContact;
            return {
              id: doc.id,
              name: data.name,
              phoneNumber: data.phoneNumber,
              email: data.email,
              relationship: data.relationship,
              isPrimary: data.isPrimary || false,
              createdAt:
                data.createdAt instanceof Timestamp
                  ? data.createdAt.toDate()
                  : new Date(data.createdAt),
              updatedAt:
                data.updatedAt instanceof Timestamp
                  ? data.updatedAt.toDate()
                  : new Date(data.updatedAt),
            };
          });

          // Sort by primary first, then by name
          contacts.sort((a, b) => {
            if (a.isPrimary && !b.isPrimary) return -1;
            if (!a.isPrimary && b.isPrimary) return 1;
            return a.name.localeCompare(b.name);
          });

          callback(contacts);
        } catch (error) {
          console.error('[Safety] Error processing snapshot:', error);
          if (!isCleanedUp) {
            try {
              callback([]);
            } catch (callbackError) {
              console.error('[Safety] Error in callback:', callbackError);
            }
          }
        }
      },
      (error) => {
        // Don't process errors if already cleaned up
        if (isCleanedUp) {
          return;
        }

        console.error('[Safety] Error subscribing to trusted contacts:', error);
        // For permission errors, don't call callback to avoid cascading errors
        const code = String((error as { code?: string })?.code ?? '');
        if (code !== 'permission-denied' && code !== 'missing-or-insufficient-permissions') {
          try {
            callback([]);
          } catch (callbackError) {
            console.error('[Safety] Error in callback:', callbackError);
          }
        }
      }
    );
  } catch (error) {
    console.error('[Safety] Error setting up subscription:', error);
    // Return a no-op cleanup function if setup fails
    return () => {};
  }

  return () => {
    if (isCleanedUp) {
      return; // Already cleaned up, prevent double cleanup
    }
    isCleanedUp = true;

    if (unsubscribeFn) {
      try {
        unsubscribeFn();
      } catch (error) {
        // Silently ignore cleanup errors - they're often due to Firestore internal state
      } finally {
        unsubscribeFn = null; // Clear reference after cleanup
      }
    }
  };
};

/**
 * Add a trusted contact
 */
export const addTrustedContact = async (
  userId: string,
  contact: Omit<TrustedContact, 'id' | 'createdAt' | 'updatedAt'>
): Promise<{ success: boolean; error?: string; contactId?: string }> => {
  try {
    // Validate phone number (basic validation)
    const cleanPhone = contact.phoneNumber.replace(/[^\d+]/g, '');
    if (cleanPhone.length < 7) {
      return { success: false, error: 'Invalid phone number' };
    }

    const contactRef = doc(collection(db, 'users', userId, 'trustedContacts'));
    const firestoreContact: Record<string, unknown> = {
      name: contact.name.trim(),
      phoneNumber: cleanPhone,
      isPrimary: contact.isPrimary || false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    if (contact.email) {
      firestoreContact.email = contact.email.trim().toLowerCase();
    }

    if (contact.relationship) {
      firestoreContact.relationship = contact.relationship.trim();
    }

    // Use a batch write to avoid edge-case Firestore internal assertion failures on iOS
    // (observed when mixing getDocs + multiple updateDoc + setDoc in quick succession).
    const batch = writeBatch(db);

    // If this is set as primary, unset other primary contacts in the same batch.
    if (contact.isPrimary) {
      const contactsRef = collection(db, 'users', userId, 'trustedContacts');
      const primaryQuery = query(contactsRef, where('isPrimary', '==', true));
      const primarySnapshot = await getDocs(primaryQuery);
      primarySnapshot.docs.forEach((d) => {
        batch.update(d.ref, { isPrimary: false, updatedAt: serverTimestamp() });
      });
    }

    batch.set(contactRef, firestoreContact);
    await batch.commit();

    return { success: true, contactId: contactRef.id };
  } catch (error) {
    console.error('[Safety] Error adding trusted contact:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to add contact',
    };
  }
};

/**
 * Update a trusted contact
 */
export const updateTrustedContact = async (
  userId: string,
  contactId: string,
  updates: Partial<Omit<TrustedContact, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const contactRef = doc(db, 'users', userId, 'trustedContacts', contactId);
    const contactDoc = await getDoc(contactRef);

    if (!contactDoc.exists()) {
      return { success: false, error: 'Contact not found' };
    }

    const updateData: Record<string, any> = { updatedAt: serverTimestamp() };

    if (updates.name !== undefined) {
      updateData.name = updates.name.trim();
    }
    if (updates.phoneNumber !== undefined) {
      const cleanPhone = updates.phoneNumber.replace(/[^\d+]/g, '');
      if (cleanPhone.length < 7) {
        return { success: false, error: 'Invalid phone number' };
      }
      updateData.phoneNumber = cleanPhone;
    }
    if (updates.email !== undefined) {
      if (updates.email) {
        updateData.email = updates.email.trim().toLowerCase();
      } else {
        // Remove field if cleared (avoid storing empty string)
        updateData.email = deleteField();
      }
    }
    if (updates.relationship !== undefined) {
      if (updates.relationship) {
        updateData.relationship = updates.relationship.trim();
      } else {
        updateData.relationship = deleteField();
      }
    }
    if (updates.isPrimary !== undefined) {
      updateData.isPrimary = updates.isPrimary;

      // If setting as primary, unset other primary contacts
      if (updates.isPrimary) {
        const contactsRef = collection(db, 'users', userId, 'trustedContacts');
        const primaryQuery = query(
          contactsRef,
          where('isPrimary', '==', true)
        );
        const primarySnapshot = await getDocs(primaryQuery);
        // Use batch to update all docs atomically-ish and avoid internal assertion edge cases.
        const batch = writeBatch(db);
        primarySnapshot.docs
          .filter((d) => d.id !== contactId)
          .forEach((d) => batch.update(d.ref, { isPrimary: false, updatedAt: serverTimestamp() }));
        batch.update(contactRef, updateData);
        await batch.commit();
        return { success: true };
      }
    }
    await updateDoc(contactRef, updateData as any);

    return { success: true };
  } catch (error) {
    console.error('[Safety] Error updating trusted contact:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update contact',
    };
  }
};

/**
 * Delete a trusted contact
 */
export const deleteTrustedContact = async (
  userId: string,
  contactId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const contactRef = doc(db, 'users', userId, 'trustedContacts', contactId);
    await deleteDoc(contactRef);

    return { success: true };
  } catch (error) {
    console.error('[Safety] Error deleting trusted contact:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete contact',
    };
  }
};

/**
 * Get a single trusted contact
 */
export const getTrustedContact = async (
  userId: string,
  contactId: string
): Promise<TrustedContact | null> => {
  try {
    const contactRef = doc(db, 'users', userId, 'trustedContacts', contactId);
    const contactDoc = await getDoc(contactRef);

    if (!contactDoc.exists()) {
      return null;
    }

    const data = contactDoc.data() as FirestoreTrustedContact;
    return {
      id: contactDoc.id,
      name: data.name,
      phoneNumber: data.phoneNumber,
      email: data.email,
      relationship: data.relationship,
      isPrimary: data.isPrimary || false,
      createdAt:
        data.createdAt instanceof Timestamp
          ? data.createdAt.toDate()
          : new Date(data.createdAt),
      updatedAt:
        data.updatedAt instanceof Timestamp
          ? data.updatedAt.toDate()
          : new Date(data.updatedAt),
    };
  } catch (error) {
    console.error('[Safety] Error getting trusted contact:', error);
    return null;
  }
};

